import { useEffect, useRef } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { formatDate } from './format';
import { shortSha } from './appDiff';

function headline(change) {
  if (change.kind === 'unknown') return 'The source of this run\'s test app is unknown.';
  if (change.kind === 'same') return 'Same test app build as the previous run.';
  if (change.kind === 'link') return 'The commit list was not recorded for this pair of runs.';
  if (change.status === 'behind') return 'This run tested an older build than the previous run.';
  if (change.totalCommits === null || change.totalCommits === undefined) return 'The commit list could not be read from GitHub.';
  const count = change.totalCommits;
  const shown = change.truncated ? ` (latest ${change.commits.length} shown)` : '';
  return `${count} commit${count === 1 ? '' : 's'} since the previous run${shown}.`;
}

export function AppChangeDialog({ change, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!change) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [change, onClose]);

  if (!change) return null;
  const repo = change.repo ? change.repo.split('/').pop() : null;
  return (
    <div
      className="sdkp-dialog-overlay"
      ref={overlayRef}
      data-testid="app-change-overlay"
      onMouseDown={(event) => { if (event.target === overlayRef.current) onClose(); }}
    >
      <div className="sdkp-dialog" role="dialog" aria-modal="true" aria-label="Test app changes" data-panel="app-change">
        <div className="sdkp-dialog-head">
          <div>
            <h3 className="sdkp-dialog-title">
              Test app changes: {formatDate(change.previousRun.uploadedAt)} → {formatDate(change.run.uploadedAt)}
            </h3>
            {repo && (
              <div className="sdkp-app-change-range">
                {repo} <code>{shortSha(change.base)}</code> → <code>{shortSha(change.head)}</code>
                {change.branch && ` (${change.branch})`}
                {change.htmlUrl && (
                  <>
                    {' · '}
                    <a href={change.htmlUrl} target="_blank" rel="noopener noreferrer">
                      Compare on GitHub <ExternalLink size={12} />
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
          <button type="button" className="sdkp-dialog-close" onClick={onClose} aria-label="Close test app changes">
            <X size={16} />
          </button>
        </div>
        <div className="sdkp-dialog-body">
          <p className="sdkp-app-change-summary">{headline(change)}</p>
          {change.commits.length > 0 && (
            <table className="sdkp-table sdkp-app-change-commits">
              <thead>
                <tr>
                  <th>Commit</th>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {change.commits.map((commit) => (
                  <tr key={commit.sha}>
                    <td>
                      {commit.url
                        ? <a href={commit.url} target="_blank" rel="noopener noreferrer"><code>{shortSha(commit.sha)}</code></a>
                        : <code>{shortSha(commit.sha)}</code>}
                    </td>
                    <td className="sdkp-app-change-title">{commit.title}</td>
                    <td>{commit.author || '-'}</td>
                    <td>{commit.date ? formatDate(commit.date) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
